import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
export const InputType = {
  number: ItemCreationValidInput.NUMBER,
  text: ItemCreationValidInput.TEXT,
  money: ItemCreationValidInput.MONEY,
  date: ItemCreationValidInput.DATE,
  time: ItemCreationValidInput.TIME,
  select: ItemCreationValidInput.SELECT,
  period: ItemCreationValidInput.PERIOD,
  limited_period: ItemCreationValidInput.LIMITED_PERIOD,
  checkbox: ItemCreationValidInput.CHECKBOX,
  color: ItemCreationValidInput.COLOR,
  trainer_selector: ItemCreationValidInput.TRAINER_SELECTOR,
  location_selector: ItemCreationValidInput.LOCATION_SELECTOR,
  bookinggroup_selector: ItemCreationValidInput.BOOKINGGROUP_SELECTOR,
  switcher: ItemCreationValidInput.SWITCHER,
  counter: ItemCreationValidInput.COUNTER,
  location_selector_multi: ItemCreationValidInput.LOCATION_SELECTOR_MULTI,
} as any;

export type ActualRate = {
  uuid: string;
  merchantPayoutRateUuid: string;
  merchantUuid: string;
  name: string;
  amountNet: number;
  amountGross: number;
  currency: string;
  startAt: string; // Use 'string' for ISO date format; alternatively, 'Date' if you want to handle it as a Date object.
  endAt: string | null; // Can be a string (ISO date format) or null.
  selected?: boolean;
};

export type MerchantPayoutRate = {
  uuid: string;
  merchantUuid: string;
  name: string;
  type: number;
  actualRate: ActualRate; // Use the existing ActualRate type
  nextRate: ActualRate | null; // Next rate can also be an ActualRate or null
};

export enum MerchantStatus {
  billable = 'billable',
  notBillable = 'notBillable',
}

type MerchantDetailsAPI = {
  uscId?: number;
  code: string;
  name: string;
  address: string;
  vatNumber: string;
  accountIban: string;
  swift: string;
  sortCode: string;
  enableVat: boolean;
  zip: string;
  city: string;
  rcsPlace: string;
  rcsN: string;
  siret: string;
  siren: string;
  beneficiary: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  contactWeb: string;
  contactAuCapital: string;
  isBillable: boolean;
  countryCode: string;
  notes: string;
  legalRepresentative: string;
  taxId: string;
  placeOfBirth: string;
  businessType: string;
  lastConfirmedAt?: string | null;
  commercialRegisterNumber: string;
  naturalPersonDateOfBirth?: string | null;
  venueId: number;
  vatValue?: number;
  countryVatValue?: number;
  merchantDataComplete?: boolean;
  withholdingTaxRate?: number;
};

export type IBANValidationParams = {
  iban: string;
};
export type IMerchantIBANValidationResponse = {
  isValid?: boolean;
  error?: string;
};
export type IMerchantAPI = {
  uuid: string;
  details: MerchantDetailsAPI;
  locations?: number[];
  clientId?: string;
  defaultSinglePayoutRate?: number | string;
  defaultFlatPayoutRate?: number;
};

export type IMerchant = {
  uuid: string;
  name: string;
  country: string;
  entities: string;
  address: string;
  postcode: string;
  merchantId: string;
  city: string;
  taxId?: string;
  birthDate?: string;
  birthPlace?: string;
  vatId?: string;
  iban?: string;
  bic?: string;
  bankAccountHolder?: string;
  registerNumber?: string;
  legalRepresentative?: string;
  locationIds?: string[] | null;
  consent?: boolean;
  billable?: boolean;
  status?: MerchantStatus;
  customField?: string;
  venueId?: number;
  lastConfirmedAt?: string;
  vatValue?: number;
  countryVatValue?: number;
  merchantDataComplete?: boolean;
  withholdingTaxRate?: number;
  defaultSinglePayoutRate?: number | string;
  defaultFlatPayoutRate?: number;
};

export type DependentField = {
  field: string;
  value: string[];
};
export type Dependency = DependentField[];

export type FormField = {
  name: string;
  label: string;
  type: string;
  required?: boolean | Dependency | Dependency[];
  options?: any[];
  dependency?: Dependency | Dependency[];
  groupBy?: number;
};

export type FormGroups = {
  value: number;
  label: string;
  tooltip?: string;
};

export const initialValues = ({
  uuid: '',
  country: '',
  entities: 'personal',
  name: '',
  address: '',
  postcode: '',
  city: '',
  taxId: '',
  birthDate: new Date(),
  birthPlace: '',
  vatId: '',
  registerNumber: '',
  legalRepresentative: '',
  iban: '',
  bic: '',
  bankAccountHolder: '',
  status: MerchantStatus.billable,
  locationIds: [],
  consent: false,
  billable: true,
  customField: '',
} as unknown) as IMerchant;

export const mapMerchantAPI2App = (data: IMerchantAPI): IMerchant => {
  const birthDate = data?.details?.naturalPersonDateOfBirth
    ? new Date(data?.details?.naturalPersonDateOfBirth).toISOString()
    : null;
  const { vatNumber, rcsN, siren, siret } = data?.details;

  const legalTypes = {
    legal_entity: 'legal',
    natural_person: 'personal',
  } as any;

  const businessType = !!data?.details?.businessType?.length
    ? legalTypes?.[data?.details?.businessType]
    : null;

  const status =
    !!data?.details.merchantDataComplete &&
    (data?.details?.isBillable ? MerchantStatus.billable : MerchantStatus.notBillable);
  return {
    merchantId: data.details.code,
    uuid: data?.uuid,
    name: data?.details?.name,
    country: data?.details?.countryCode?.toLowerCase(),
    entities: businessType,
    address: data?.details?.address,
    postcode: data?.details?.zip,
    city: data?.details?.city,
    taxId: data?.details?.taxId,
    birthDate,
    birthPlace: data?.details?.placeOfBirth,
    vatId: vatNumber || rcsN || siren || siret,
    iban: data?.details?.accountIban,
    bic: data?.details?.swift,
    bankAccountHolder: data?.details?.beneficiary,
    registerNumber: data?.details?.commercialRegisterNumber,
    legalRepresentative: data?.details?.legalRepresentative,
    locationIds: data?.locations?.map((id) => `${id}`) || [],
    billable: data?.details?.isBillable,
    status,
    venueId: data?.details?.venueId || 0,
    lastConfirmedAt: data?.details?.lastConfirmedAt || new Date().toISOString(),
    vatValue: data?.details?.vatValue || 0,
    countryVatValue: data?.details?.enableVat || 0,
    customField: data?.details?.notes || '',
    merchantDataComplete: data?.details?.merchantDataComplete,
    defaultSinglePayoutRate: data?.defaultSinglePayoutRate || 0,
    defaultFlatPayoutRate: data?.defaultFlatPayoutRate || 0,
    withholdingTaxRate: data?.details?.withholdingTaxRate || 0,
  } as IMerchant;
};

export const mapMerchantAPP2API = (data: IMerchant, original?: IMerchantAPI): IMerchantAPI => {
  const country = data?.country?.toUpperCase();
  const legalTypes = {
    legal: 'legal_entity',
    personal: 'natural_person',
  } as any;
  const businessType = legalTypes?.[data?.entities];
  return {
    ...original,
    uuid: data?.uuid,
    details: {
      ...(original?.details || {}),
      name: data?.name,
      countryCode: data?.country?.toUpperCase(),
      businessType,
      address: data?.address,
      zip: data?.postcode,
      city: data?.city,
      taxId: data?.taxId,
      placeOfBirth: data?.birthPlace,
      naturalPersonDateOfBirth: data?.birthDate ? new Date(data?.birthDate).toISOString() : null,
      isBillable: data?.billable,
      beneficiary: data?.bankAccountHolder,
      accountIban: data?.iban,
      swift: data?.bic,
      commercialRegisterNumber: data?.registerNumber,
      legalRepresentative: data?.legalRepresentative,
      vatNumber: data?.vatId,
      siren: country === 'FR' && data?.entities === 'legal' ? data?.taxId : '',
      siret: country === 'FR' && data?.entities !== 'legal' ? data?.taxId : '',
      rcsN: country === 'FR' ? data?.registerNumber : '',
      venueId: data?.venueId || 0,
      lastConfirmedAt: data?.lastConfirmedAt || new Date().toISOString(),
      vatValue: data?.vatValue || 0,
      countryVatValue: data.countryVatValue || 0,
      notes: data?.customField || '',
      withholdingTaxRate: data?.withholdingTaxRate || 0,
    },
    clientId: 'Fitogram',
    defaultSinglePayoutRate: data?.defaultSinglePayoutRate,
    defaultFlatPayoutRate: 0,
  } as IMerchantAPI;
};
